/**
 * ASN list table extracted from ASNListTab to keep each file under the
 * 500-line limit. Renders the sortable column headers and the ASN rows,
 * delegating sort + view actions back to the parent. Rows the API marks
 * `can_edit_details` also get an edit control for the shipment fields.
 */
import { Eye, Sparkles, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { AdvancedShippingNotice } from '../../../lib/api';
import { CourierStatusBadge, isTrackableASN } from '../tracking/CourierStatusBadge';
import ASNEditDetails from '../ASNEditDetails';
import { formatCourier } from '../asnCourier';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  column: string;
  direction: SortDirection;
}

interface ASNListTableProps {
  asns: AdvancedShippingNotice[];
  sortConfig: SortConfig;
  counterpartyLabel: string;
  isSupplierPortalMode: boolean;
  onSort: (column: string) => void;
  onViewDetails: (asn: AdvancedShippingNotice) => void;
  /** Open an ASN straight on its courier tracking timeline. */
  onViewTracking: (asn: AdvancedShippingNotice) => void;
  /** Refetch the list after a row's shipment details were edited. */
  onEdited: () => void;
  formatDate: (dateString: string) => string;
  formatDateTime: (dateString: string) => string;
  /** Ids of rows currently selected for the bulk Mark Delivered action. */
  selectedIds: Set<number>;
  /** Whether a given ASN row is eligible for selection (in_transit only). */
  isSelectable: (asn: AdvancedShippingNotice) => boolean;
  /** Toggle selection of a single ASN row. */
  onToggleSelect: (asn: AdvancedShippingNotice) => void;
  /** Toggle selection of all selectable rows on the current page. */
  onToggleSelectAll: () => void;
}

/**
 * Status pill for an ASN row.
 */
function StatusBadge({ status }: { status: string }) {
  const statusMap: Record<string, { label: string; class: string }> = {
    created: { label: 'Created', class: 'bg-gray-100 text-gray-800' },
    submitted: { label: 'Submitted', class: 'bg-blue-100 text-blue-800' },
    in_transit: { label: 'In Transit', class: 'bg-yellow-100 text-yellow-800' },
    delivered: { label: 'Delivered', class: 'bg-green-100 text-green-800' },
    // Poeton does not use the 'Received' ASN state ('Delivered' is terminal).
    // The backend status is retained, but if a stray received row exists we
    // surface it as 'Delivered' so 'Received' never appears in the UI.
    received: { label: 'Delivered', class: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
  };
  const config = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
      {config.label}
    </span>
  );
}

/**
 * Sortable, clickable ASN list table. All sorting/viewing is delegated to
 * the parent via callbacks; this component holds no state.
 */
export function ASNListTable({
  asns,
  sortConfig,
  counterpartyLabel,
  isSupplierPortalMode,
  onSort,
  onViewDetails,
  onViewTracking,
  onEdited,
  formatDate,
  formatDateTime,
  selectedIds,
  isSelectable,
  onToggleSelect,
  onToggleSelectAll,
}: ASNListTableProps) {
  const selectableRows = asns.filter(isSelectable);
  const allSelectableSelected =
    selectableRows.length > 0 && selectableRows.every((a) => selectedIds.has(a.id));
  const SortableHeader = ({
    column,
    label,
    className = '',
    children,
  }: {
    column: string;
    label: string;
    className?: string;
    children?: ReactNode;
  }) => {
    const isActive = sortConfig.column === column;
    return (
      <th
        className={`text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50 select-none transition-colors ${className}`}
        onClick={() => onSort(column)}
      >
        <div className="flex items-center gap-1">
          {children || label}
          <span className="text-muted-foreground">
            {isActive ? (
              sortConfig.direction === 'asc' ? (
                <ArrowUp className="h-3.5 w-3.5" />
              ) : (
                <ArrowDown className="h-3.5 w-3.5" />
              )
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
            )}
          </span>
        </div>
      </th>
    );
  };

  return (
    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-20">
          <tr className="border-b bg-muted/80 backdrop-blur-sm">
            <th className="text-center py-3 px-3 font-medium w-[44px]">
              <input
                type="checkbox"
                aria-label="Select all in-transit ASNs on this page"
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                checked={allSelectableSelected}
                disabled={selectableRows.length === 0}
                onChange={onToggleSelectAll}
              />
            </th>
            <th className="text-left py-3 px-4 font-medium sticky left-0 bg-muted/30 z-10 min-w-[100px]">Actions</th>
            <SortableHeader column="asn_number" label="ASN Number" className="min-w-[140px]" />
            <SortableHeader column="status" label="Status" className="min-w-[140px]" />
            <SortableHeader column="customer__name" label={counterpartyLabel} className="min-w-[180px]" />
            <SortableHeader column="supplier__name" label={isSupplierPortalMode ? 'Site' : 'Supplier'} className="min-w-[180px]" />
            <th className="text-left py-3 px-4 font-medium min-w-[180px]">Company</th>
            <SortableHeader column="courier" label="Courier" className="min-w-[120px]" />
            <SortableHeader column="tracking_number" label="Tracking Number" className="min-w-[160px]" />
            <th className="text-left py-3 px-4 font-medium min-w-[160px]">
              <div className="flex items-center gap-1">
                Courier Status
                <span className="px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full font-bold flex items-center gap-0.5" title="Premium Feature - Real-time tracking via Courier API">
                  <Sparkles className="h-2.5 w-2.5" />
                  PRO
                </span>
              </div>
            </th>
            <SortableHeader column="method_of_transport" label="Transport Method" className="min-w-[140px]" />
            <SortableHeader column="weight_kg" label="Weight (kg)" className="min-w-[110px] text-right" />
            <SortableHeader column="distance_km" label="Distance (km)" className="min-w-[120px] text-right" />
            <SortableHeader column="carbon_emission_kg" label="Carbon (kg CO₂)" className="min-w-[130px] text-right" />
            <SortableHeader column="estimated_delivery_date" label="Expected Delivery" className="min-w-[160px]">
              <span>Expected Delivery</span>
              <span className="px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full font-bold flex items-center gap-0.5 ml-1" title="Premium Feature - Auto-updated via Courier API">
                <Sparkles className="h-2.5 w-2.5" />
                PRO
              </span>
            </SortableHeader>
            <SortableHeader column="actual_delivery_date" label="Actual Delivery" className="min-w-[140px]" />
            <SortableHeader column="created_at" label="Created" className="min-w-[140px]" />
          </tr>
        </thead>
        <tbody>
          {asns.map((asn: AdvancedShippingNotice) => (
            <tr
              key={asn.id}
              className={`border-b hover:bg-muted/50 transition-colors ${selectedIds.has(asn.id) ? 'bg-blue-50/50' : ''}`}
            >
              <td className="py-3 px-3 text-center">
                <input
                  type="checkbox"
                  aria-label={`Select ASN ${asn.asn_number}`}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                  checked={selectedIds.has(asn.id)}
                  disabled={!isSelectable(asn)}
                  title={isSelectable(asn) ? undefined : 'Only In Transit ASNs can be marked delivered'}
                  onChange={() => onToggleSelect(asn)}
                />
              </td>
              <td className="py-3 px-4 sticky left-0 bg-background z-10">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => onViewDetails(asn)}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View
                  </button>
                  <ASNEditDetails asn={asn} onSaved={onEdited} variant="icon" />
                </div>
              </td>
              <td className="py-3 px-4 font-medium">{asn.asn_number}</td>
              <td className="py-3 px-4"><StatusBadge status={asn.status} /></td>
              <td className="py-3 px-4">{asn.customer_details?.name || '-'}</td>
              <td className="py-3 px-4">{asn.supplier_name || asn.supplier_details?.name || '-'}</td>
              <td className="py-3 px-4">{asn.supplier_details?.name || '-'}</td>
              <td className="py-3 px-4 uppercase">
                {formatCourier(asn.courier, asn.courier_other)}
              </td>
              <td className="py-3 px-4 font-mono">{asn.tracking_number || '-'}</td>
              <td className="py-3 px-4">
                {/* Clickable when there's a timeline worth opening; a plain
                    badge otherwise, so nothing invites a dead click. */}
                {asn.tracking_summary?.status ? (
                  <button
                    onClick={() => onViewTracking(asn)}
                    className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary hover:opacity-80 transition-opacity cursor-pointer"
                    title="View courier tracking history"
                    aria-label={`View courier tracking for ASN ${asn.asn_number}`}
                  >
                    <CourierStatusBadge
                      tracking={asn.tracking_summary}
                      proEnabled={asn.tracking_pro_enabled}
                      asnStatus={asn.status}
                      trackable={isTrackableASN(asn.courier, asn.tracking_number, asn.courier_other)}
                    />
                  </button>
                ) : (
                  <CourierStatusBadge
                    tracking={asn.tracking_summary}
                    proEnabled={asn.tracking_pro_enabled}
                    asnStatus={asn.status}
                    trackable={isTrackableASN(asn.courier, asn.tracking_number, asn.courier_other)}
                  />
                )}
              </td>
              <td className="py-3 px-4 capitalize">{asn.method_of_transport || '-'}</td>
              <td className="py-3 px-4 text-right">{asn.weight_kg || '-'}</td>
              <td className="py-3 px-4 text-right">
                {/* No company on this tenant is geocoded, so the ASN's own
                    customer-to-supplier distance is never computable. Fall back
                    to the distance the courier's scan trail actually reports. */}
                {asn.distance_km || (
                  asn.tracking_summary?.route_distance_km ? (
                    <span
                      className="inline-flex items-center gap-1 justify-end"
                      title="Distance so far, summed between courier scan locations"
                    >
                      <Sparkles className="h-3 w-3 text-amber-500" />
                      {asn.tracking_summary.route_distance_km}
                    </span>
                  ) : '-'
                )}
              </td>
              <td className="py-3 px-4 text-right font-medium text-green-700">
                {asn.carbon_emission_kg || asn.calculated_carbon_emission || (
                  asn.tracking_summary?.estimated_carbon_kg ? (
                    <span
                      className="inline-flex items-center gap-1 justify-end"
                      title="Estimated from the courier's reported route"
                    >
                      <Sparkles className="h-3 w-3 text-amber-500" />
                      {asn.tracking_summary.estimated_carbon_kg}
                    </span>
                  ) : '-'
                )}
              </td>
              <td className="py-3 px-4">
                {/* Prefer the courier's live estimate when Tracking PRO has one —
                    it stays visible even when courier status is set not to
                    overwrite the ASN's own dates. */}
                {asn.tracking_summary?.estimated_delivery_date ? (
                  <span
                    className="inline-flex items-center gap-1"
                    title="Live estimate from the courier"
                  >
                    <Sparkles className="h-3 w-3 text-amber-500" />
                    {formatDate(asn.tracking_summary.estimated_delivery_date)}
                  </span>
                ) : (
                  formatDate(asn.estimated_delivery_date || '')
                )}
              </td>
              <td className="py-3 px-4">{formatDate(asn.actual_delivery_date || '')}</td>
              <td className="py-3 px-4">{formatDateTime(asn.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
